import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Info } from 'lucide-react';

interface BatchModeControlsProps {
  isBatchMode: boolean;
  onBatchModeChange: (enabled: boolean) => void;
  imagesPerCollage: number;
  onImagesPerCollageChange: (value: number) => void;
  totalImages: number;
}

const BatchModeControls = ({
  isBatchMode,
  onBatchModeChange,
  imagesPerCollage,
  onImagesPerCollageChange,
  totalImages,
}: BatchModeControlsProps) => {
  const collageCount = Math.floor(totalImages / imagesPerCollage);
  const remainingImages = totalImages % imagesPerCollage;

  return (
    <div className="bg-white p-6 rounded-[24px] shadow-lg border-2 border-[#9B87F5] space-y-4 animate-fade-in">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Switch
              id="batch-mode"
              checked={isBatchMode}
              onCheckedChange={onBatchModeChange}
            />
            <Label htmlFor="batch-mode" className="text-lg font-bold text-[#6B4EE6] cursor-pointer">
              Batch Mode
            </Label>
          </div>
        </div>
        <p className="text-sm text-[#6B4EE6]/80 pl-12">
          Upload multiple images and automatically create multiple collages. For example: upload 27 images with 3 photos per collage = 9 collages in a ZIP file.
        </p>
      </div>

      {isBatchMode && (
        <>
          <div className="flex items-center gap-4">
            <Label className="text-[#6B4EE6] font-medium whitespace-nowrap">
              Images per collage:
            </Label>
            <Select
              value={imagesPerCollage.toString()}
              onValueChange={(value) => onImagesPerCollageChange(parseInt(value))}
            >
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <SelectItem key={num} value={num.toString()}>
                    {num}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <p className="text-sm text-[#6B4EE6]/70 italic">
            💡 Tip: 2 images per collage is perfect for creating front and back photos of objects or cards.
          </p>

          {totalImages > 0 && (
            <div className="bg-[#F2FCE2] p-4 rounded-[16px] border-2 border-[#9B87F5]/30">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-[#6B4EE6] mt-0.5 flex-shrink-0" />
                <div className="text-sm text-[#6B4EE6]">
                  <p className="font-bold">
                    {collageCount} collage{collageCount !== 1 ? 's' : ''} will be created
                  </p>
                  {remainingImages > 0 && (
                    <p className="mt-1 opacity-80">
                      {remainingImages} image{remainingImages !== 1 ? 's' : ''} will be excluded
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BatchModeControls;
